<template>
  <RevForm
    :initial-values
    method="POST"
    :validate="validate()"
    @on-submit-failed="handleSubmitFailed"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <RevCard class="p-24">
        <h2
          v-if="withTitle"
          class="heading-3 mb-12 md:mb-24"
          data-qa="shipping-form-title"
        >
          {{ i18n(translations.shippingAddressTitle) }}
        </h2>
        <DynamicAddressFieldValidatorsErrorLogger
          :errors
          :form-values="values"
          logger-scope="CHECKOUT_SHIPPING"
        />
        <AddressFields
          v-model:city="values.city"
          v-model:company="values.company"
          v-model:country="values.country"
          v-model:first-name="values.firstName"
          v-model:first-name-pronunciation="values.firstNamePronunciation"
          v-model:last-name="values.lastName"
          v-model:last-name-pronunciation="values.lastNamePronunciation"
          v-model:national-id="values.nationalId"
          v-model:phone="values.phone"
          v-model:postal-code="values.postalCode"
          v-model:state-or-province="values.stateOrProvince"
          v-model:street="values.street"
          v-model:street2="values.street2"
          :countries="shippableCountries"
          :default-country="MARKET_COUNTRY_CODE"
          :errors="getErrors(values, errors)"
          :feature-code="FeatureCode.WEB_CHECKOUT_SHIPPING"
          :input-autocomplete-aria-describedby-id="addressConfirmationWarningId"
          :optional-fields="['company']"
          :phone-help-message="i18n(translations.phoneHelperMessageAlternative)"
          shipping-state
          @autocompleted="handleAddressAutocomplete"
          @update:country="(country) => emit('country-change', country)"
          @update:postal-code="handlePostalCodeUpdate"
          @update:street="handleStreetUpdate"
        />

        <RevInfoBlock
          v-if="shouldDisplayConfirmationWarning"
          :id="addressConfirmationWarningId"
          class="mt-7"
          :content="i18n(translations.confirmationWarningContent)"
          data-qa="confirmation-warning"
          :icon="IconWarning"
          role="alert"
          :title="i18n(translations.confirmationWarningTitle)"
          variant="warning"
        >
          <Address
            :address="values"
            class="mt-16"
            compact
            :with-phone="false"
            withDetails
          />
        </RevInfoBlock>

        <RevInfoBlock
          v-if="shouldDisplayStreetNumberWarning"
          class="mt-7"
          :content="i18n(translations.streetNumberWarningContent)"
          :icon="IconWarning"
          :title="i18n(translations.streetNumberWarningTitle)"
          variant="warning"
        />

        <div v-if="shouldDisplayUserInformation(values.country as Country)">
          <RevDivider class="my-24" />
          <h2 class="heading-3 mb-16">
            {{ i18n(translations.userInformationTitle) }}
          </h2>

          <UserInformationFields
            v-model:birthdate="values.birthdate"
            v-model:national-id="values.nationalId"
            :country="values.country"
            :errors
            :with-birthdate="withUserInformation"
            :with-national-id="
              userInformationWithNationalId(values.country as Country)
            "
          />

          <slot v-bind="{ country: values.country }" />

          <RevCheckbox
            v-if="withSameBillingCheckbox"
            id="sameAsShipping"
            v-model="sameAsShipping"
            class="mt-40 md:mt-24"
          >
            <template #label>
              {{ i18n(translations.sameBillingAddressLabel) }}
            </template>
          </RevCheckbox>

          <div class="mt-16 flex flex-col items-stretch md:items-end">
            <RevButton
              data-qa="submit-button"
              :disabled="submitting"
              full-width="adaptive"
              type="submit"
              variant="primary"
              @click="onSubmitClick"
            >
              {{ checkoutContentCTA }}
            </RevButton>
          </div>
        </div>

        <div v-if="!shouldDisplayUserInformation(values.country as Country)">
          <slot v-bind="{ country: values.country }" />
        </div>

        <div
          v-if="
            !withUserInformation &&
            !shouldDisplayUserInformation(values.country as Country)
          "
          class="mt-40 flex flex-col items-stretch md:mt-24 md:items-end"
        >
          <RevCheckbox
            v-if="withSameBillingCheckbox"
            id="sameAsShipping"
            v-model="sameAsShipping"
            class="self-start"
          >
            <template #label>
              {{ i18n(translations.sameBillingAddressLabel) }}
            </template>
          </RevCheckbox>

          <RevButton
            class="mt-16"
            data-qa="submit-button"
            :disabled="submitting"
            full-width="adaptive"
            type="submit"
            variant="primary"
            @click="onSubmitClick"
          >
            {{ checkoutContentCTA }}
          </RevButton>
        </div>
      </RevCard>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { useRageEventsDetection } from '#imports'
import { computed, reactive, ref } from 'vue'

import { Country } from '@backmarket/http-api'
import type { AddressFieldsValues } from '@backmarket/http-api/src/api-specs-shipping/address/address-validation.types'
import type {
  Deliver,
  UserInformation,
} from '@backmarket/nuxt-layer-checkout/api/cart/types'
import Address from '@backmarket/nuxt-module-address/Address.vue'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import { InputAddressPhoneValidators } from '@backmarket/nuxt-module-address/InputAddressPhoneValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { isNationalIdShownForBilling } from '@backmarket/nuxt-module-address/utils/nationalId/isNationalIdShownForBilling'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import type { StripNullish } from '@backmarket/utils/types/StripNullish'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevDivider } from '@ds/components/Divider'
import { type ErrorsOf, RevForm, makeValidate } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import {
  shouldValidatePostalCodeIfAutocompleted,
  shouldValidateStreetIfAutocompleted,
  shouldValidateStreetNumber,
} from '~/scopes/checkout/utils/autocomplete'
import { focusFirstErrorFormField } from '~/scopes/checkout/utils/focusFirstErrorFormField'

import translations from './ShippingAddressForm.translations'

const props = withDefaults(
  defineProps<{
    submitting?: boolean
    address: Deliver
    apiErrors: { [key: string]: string }
    countries: {
      value: string
      label: string
    }[]
    withUserInformation?: boolean
    withSameBillingCheckbox?: boolean
    userInformation: Partial<UserInformation>
    withTitle?: boolean
    submitButtonLabel?: string
    withNationalId?: boolean
  }>(),
  {
    submitting: false,
    withUserInformation: false,
    withSameBillingCheckbox: false,
    withTitle: true,
    submitButtonLabel: '',
    withNationalId: true,
  },
)

const i18n = useI18n()

// @TODO: Need to do this cast because MarketCountryCode and Country are not compatible
const MARKET_COUNTRY_CODE = useMarketplace().market
  .countryCode as unknown as Country

const { getDynamicAddressFieldValidators } = useDynamicAddressFieldValidators()

type FormValues = Required<
  StripNullish<
    Pick<
      AddressFieldsValues,
      | 'city'
      | 'company'
      | 'country'
      | 'firstName'
      | 'lastName'
      | 'phone'
      | 'postalCode'
      | 'stateOrProvince'
      | 'street'
      | 'street2'
      | 'nationalId'
    >
  >
> &
  Pick<
    AddressFieldsValues,
    'firstNamePronunciation' | 'lastNamePronunciation' | 'birthdate'
  >

const initialValues = reactive<FormValues>({
  city: props.address.city ?? '',
  company: props.address.company ?? '',
  country: props.countries.some(({ value }) => value === props.address.country)
    ? (props.address.country as Country)
    : MARKET_COUNTRY_CODE,
  firstName: props.address.firstName ?? '',
  lastName: props.address.lastName ?? '',
  nationalId: props.userInformation.taxId ?? '',
  phone: props.address.phone ?? '',
  postalCode: props.address.postalCode ?? '',
  stateOrProvince: props.address.stateOrProvince ?? '',
  street: props.address.street ?? '',
  street2: props.address.street2 ?? '',
  ...insertIf(props.withUserInformation, {
    birthdate: props.userInformation.birthdate ?? props.address.birthdate ?? '',
  }),
  ...insertIf(props.address.country === Country.JP, {
    firstNamePronunciation: props.address.firstNamePronunciation ?? '',
    lastNamePronunciation: props.address.lastNamePronunciation ?? '',
  }),
})

const isAddressAutocompleted = ref(false)
const isAddressPotentiallyWrong = ref(false)
const isSubmittedOnce = ref(false)
const sameAsShipping = ref(props.withSameBillingCheckbox)
const shouldDisplayStreetNumberWarning = ref(false)

const addressConfirmationWarningId = 'address-confirmation-warning'

const emit = defineEmits(['submit', 'submit-failed', 'country-change'])

emit('country-change', initialValues.country)

const checkoutContentCTA = computed(() => {
  if (props.submitButtonLabel) {
    return props.submitButtonLabel
  }

  return sameAsShipping.value
    ? i18n(translations.submit)
    : i18n(translations.submitBilling)
})

const shouldDisplayConfirmationWarning = computed(() => {
  return isAddressPotentiallyWrong.value && isSubmittedOnce.value
})

const addressValidation = await getDynamicAddressFieldValidators({
  initialFormValues: initialValues,
  metadata: {
    scope: 'customer',
  },
})

const validators = {
  ...addressValidation,
  phone: InputAddressPhoneValidators(i18n),
  birthdate: InputAddressBirthdateValidators(i18n),
  ...insertIf(sameAsShipping.value, {
    nationalId: InputAddressNationalIdValidators(i18n),
  }),
}

// Memoize the validate function to return the same instance across renders.
// This is needed because the RevForm component watches props.validate and executes makeErrorsVisible()
// every time the signature of the validators changed.
const validateFn = makeValidate<FormValues>(validators)

function validate() {
  return validateFn
}

const logger = useLogger()
const { recordEvent } = useRageEventsDetection()

function onSubmitClick(event: Event) {
  const isRageEvent = recordEvent(event)

  if (isRageEvent) {
    logger.info('[Checkout] Rage click detected', {
      owners: ['bot-squad-checkout-front'],
    })
  }
}

const shippableCountries = computed(() => {
  if (isEmpty(props.countries)) {
    return [
      {
        label: i18n.country(MARKET_COUNTRY_CODE),
        value: MARKET_COUNTRY_CODE,
      },
    ]
  }

  return props.countries
})

const userInformationWithNationalId = (selectedCountry: Country) => {
  if (!props.withNationalId) return false

  return sameAsShipping.value && isNationalIdShownForBilling(selectedCountry)
}

const getErrors = (
  values: typeof initialValues,
  errors: Partial<Record<keyof FormValues, string>>,
) => {
  const tempErrors = { ...errors, ...props.apiErrors }

  if (userInformationWithNationalId(values.country as Country)) {
    delete tempErrors.nationalId
  }

  return tempErrors
}

const handleSubmitFailed = ({ errors }: { errors: ErrorsOf<FormValues> }) => {
  focusFirstErrorFormField(errors)

  emit('submit-failed', {
    ...errors,
  })
}

const handleSubmit = (values: FormValues) => {
  if (shouldDisplayConfirmationWarning.value) {
    isAddressPotentiallyWrong.value = false
  }

  isSubmittedOnce.value = true

  const taxId =
    sameAsShipping.value &&
    isNationalIdShownForBilling(values.country as Country) &&
    !isEmpty(values.nationalId)
      ? values.nationalId
      : undefined

  if (!isAddressPotentiallyWrong.value) {
    emit(
      'submit',
      {
        city: values.city,
        company: values.company,
        country: values.country,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        postalCode: values.postalCode,
        stateOrProvince: values.stateOrProvince,
        street: values.street,
        street2: values.street2,
        birthdate: values.birthdate,
        ...insertIf(props.address.country === Country.JP, {
          firstNamePronunciation: values.firstNamePronunciation,
          lastNamePronunciation: values.lastNamePronunciation,
        }),
      },
      sameAsShipping.value,
      {
        birthdate: values.birthdate,
        ...insertIf(!isEmpty(taxId), { taxId }),
      },
    )
  }
}

const handleAddressAutocomplete = (address: { [key: string]: string }) => {
  isAddressAutocompleted.value = true
  isAddressPotentiallyWrong.value = false

  if (shouldValidateStreetNumber({ country: MARKET_COUNTRY_CODE })) {
    shouldDisplayStreetNumberWarning.value = !/\d/.test(address.street)
  }
}

const handleStreetUpdate = () => {
  if (shouldValidateStreetIfAutocompleted({ country: MARKET_COUNTRY_CODE })) {
    isAddressAutocompleted.value = false
    isAddressPotentiallyWrong.value = true
  }
}

const handlePostalCodeUpdate = () => {
  if (
    shouldValidatePostalCodeIfAutocompleted({ country: MARKET_COUNTRY_CODE })
  ) {
    isAddressAutocompleted.value = false
    isAddressPotentiallyWrong.value = true
  }
}

const shouldDisplayUserInformation = (country: Country) => {
  return props.withUserInformation || userInformationWithNationalId(country)
}
</script>
